/**
 * Read-only value + copy button, used for the SP endpoints and the one-time
 * SCIM bearer token on the Identity Providers page.
 *
 * `navigator.clipboard` is unavailable on insecure origins and in some
 * embedded browsers, so the value is always selectable text and the button
 * degrades to "Select" rather than disappearing.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { DeskSection } from '../../components/desk-section';

export interface CopyFieldProps {
  label: string;
  value: string;
  /** Masks the value until the user reveals it (bearer tokens). */
  isSecret?: boolean;
}

const RESET_AFTER_MS = 2000;

export function CopyField({ label, value, isSecret = false }: CopyFieldProps): JSX.Element {
  const [isCopied, setIsCopied] = useState(false);
  const [isRevealed, setIsRevealed] = useState(!isSecret);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isCopied) return undefined;
    const timer = setTimeout(() => setIsCopied(false), RESET_AFTER_MS);
    return () => clearTimeout(timer);
  }, [isCopied]);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(value);
      setIsCopied(true);
    } catch {
      // Clipboard blocked: fall back to selecting the text so ⌘/Ctrl+C works.
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }

  return (
    <div className="field copy-field">
      <label className="field__label" htmlFor={`copy-${label}`}>
        {label}
      </label>
      <div className="copy-field__row">
        <input
          id={`copy-${label}`}
          ref={inputRef}
          className="field__control"
          readOnly
          type={isRevealed ? 'text' : 'password'}
          value={value}
          onFocus={(event) => event.target.select()}
        />
        {isSecret ? (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setIsRevealed((current) => !current)}
          >
            {isRevealed ? 'Hide' : 'Show'}
          </button>
        ) : null}
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => void copy()}>
          {isCopied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <span aria-live="polite" className="visually-hidden">
        {isCopied ? `${label} copied to the clipboard` : ''}
      </span>
    </div>
  );
}

export interface ServiceProviderEndpoints {
  saml_acs_url: string;
  saml_entity_id: string;
  saml_metadata_url: string;
  scim_base_url: string;
}

export interface ServiceProviderDetailsProps {
  endpoints: ServiceProviderEndpoints;
  /** Defaults to the "paste these into your IdP" line. */
  description?: ReactNode;
  /** Optional deployment note under the fields. */
  note?: ReactNode;
}

/** The four SP values a school pastes into its IdP, as a compact label/value form. */
export function ServiceProviderDetails({
  endpoints,
  description = 'Paste these into your identity provider. They are the same for every provider you add.',
  note = null,
}: ServiceProviderDetailsProps): JSX.Element {
  return (
    <DeskSection
      className="federation__endpoints"
      title="Service provider details"
      description={description}
    >
      <div className="desk-form federation__endpoint-fields">
        <CopyField label="SAML ACS (reply) URL" value={endpoints.saml_acs_url} />
        <CopyField label="SAML entity ID (audience)" value={endpoints.saml_entity_id} />
        <CopyField label="SP metadata URL" value={endpoints.saml_metadata_url} />
        <CopyField label="SCIM 2.0 base URL" value={endpoints.scim_base_url} />
      </div>
      {note === null ? null : <p className="muted federation__note">{note}</p>}
    </DeskSection>
  );
}
